import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Table, TableCell, TableHead, TableHeader, TableRow, TableWrap } from '@/components/ui/Table';
import { EditableSuggestedBidCell } from '@/components/recommendations/EditableSuggestedBidCell';
import { RecommendationBulkBar } from '@/components/recommendations/RecommendationBulkBar';
import { RecommendationReasonTooltip } from '@/components/recommendations/RecommendationReasonTooltip';
import { RecommendationTableToolbar } from '@/components/recommendations/RecommendationTableToolbar';
import {
  adGroupLabel,
  campaignDetailPath,
  formatRecommendationCurrency,
  formatRecommendationPct,
  keywordRowKey,
} from '@/api/recommendationFormat';
import { keywordRecommendationFingerprint } from '@/api/recommendationDismiss';
import {
  actionTypeLabel,
  filterBySearch,
  sortByMetric,
  stripDuplicateRoasFromReason,
  type RecommendationSortField,
  type RecommendationSortOrder,
} from '@/api/recommendationUtils';
import { getKeywordNegativeAlternate } from '@/api/keywordRecommendationApply';
import type { KeywordRecommendationItem, SpKeywordApplyAction } from '@/types/keywordRecommendations';
import { useMemo } from 'react';

type RecommendationTableVariant = 'profile' | 'campaign-scoped';

type KeywordRecommendationsTableProps = {
  profileId: string;
  rows: KeywordRecommendationItem[];
  selected: Set<string>;
  bidOverrides: Record<string, number>;
  applying: boolean;
  searchQuery: string;
  sortBy: RecommendationSortField;
  sortOrder: RecommendationSortOrder;
  onSearchChange: (value: string) => void;
  onSortByChange: (value: RecommendationSortField) => void;
  onSortOrderChange: (value: RecommendationSortOrder) => void;
  onToggleSelect: (key: string) => void;
  onSelectAll: (keys: string[]) => void;
  onBidChange: (key: string, bid: number) => void;
  onApply: (action: SpKeywordApplyAction) => void;
  onDismiss: (fingerprints: string[]) => void;
  onAddNegative: (row: KeywordRecommendationItem) => void;
  onBulkApply: () => void;
  onBulkDismiss: () => void;
  onClearSelection: () => void;
  buildApplyAction: (
    row: KeywordRecommendationItem,
    bidOverrides: Record<string, number>
  ) => SpKeywordApplyAction | null;
  variant?: RecommendationTableVariant;
  adGroupNameById?: Map<string, string>;
  applyDisabled?: boolean;
  applyDisabledReason?: string;
};

const DEFAULT_APPLY_DISABLED_REASON =
  'Apply is available for Sponsored Products keywords only.';

export function KeywordRecommendationsTable({
  profileId,
  rows,
  selected,
  bidOverrides,
  applying,
  searchQuery,
  sortBy,
  sortOrder,
  onSearchChange,
  onSortByChange,
  onSortOrderChange,
  onToggleSelect,
  onSelectAll,
  onBidChange,
  onApply,
  onDismiss,
  onAddNegative,
  onBulkApply,
  onBulkDismiss,
  onClearSelection,
  buildApplyAction,
  variant = 'profile',
  adGroupNameById,
  applyDisabled = false,
  applyDisabledReason = DEFAULT_APPLY_DISABLED_REASON,
}: KeywordRecommendationsTableProps) {
  const isCampaignScoped = variant === 'campaign-scoped';

  const filteredRows = useMemo(() => {
    const searched = filterBySearch(rows, searchQuery, (row) => {
      const adGroupName = adGroupLabel(row.adGroupId, adGroupNameById);
      if (isCampaignScoped) {
        return [row.keywordText, adGroupName, row.adGroupId];
      }
      return [row.keywordText, row.campaignName || '', row.campaignId, adGroupName, row.adGroupId];
    });
    return sortByMetric(searched, sortBy, sortOrder, (row) => row.metrics);
  }, [rows, searchQuery, sortBy, sortOrder, isCampaignScoped, adGroupNameById]);

  const selectableKeys = useMemo(
    () =>
      filteredRows
        .map((row) => keywordRowKey(row.keywordId))
        .filter((_, index) => buildApplyAction(filteredRows[index], bidOverrides) != null),
    [filteredRows, bidOverrides, buildApplyAction]
  );

  const allSelected =
    selectableKeys.length > 0 && selectableKeys.every((key) => selected.has(key));
  const someSelected = selectableKeys.some((key) => selected.has(key));

  if (!rows.length) {
    return (
      <section className="overflow-hidden rounded-xl border border-border bg-surface">
        <div className="border-b border-border bg-page px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Keyword recommendations</h3>
          <p className="mt-0.5 text-xs text-foreground-muted">Bid and pause suggestions for existing keywords.</p>
        </div>
        <p className="px-4 py-6 text-sm text-foreground-subtle">No keyword-level actions for this range.</p>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="border-b border-border bg-page px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">Keyword recommendations</h3>
        <p className="mt-0.5 text-xs text-foreground-muted">Bid and pause suggestions for existing keywords.</p>
      </div>

      <RecommendationTableToolbar
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        actionFilter="ALL"
        onActionFilterChange={() => {}}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortByChange={onSortByChange}
        onSortOrderChange={onSortOrderChange}
        showingCount={filteredRows.length}
        totalCount={rows.length}
        searchPlaceholder={
          isCampaignScoped ? 'Keyword or ad group…' : 'Keyword, campaign, or ad group…'
        }
      />

      <div className="px-3 pt-2">
        <RecommendationBulkBar
          selectedCount={selected.size}
          onApplySelected={onBulkApply}
          onDismissSelected={onBulkDismiss}
          onClearSelection={onClearSelection}
          applying={applying}
          applyDisabled={applyDisabled}
          applyDisabledReason={applyDisabledReason}
        />
      </div>

      <TableWrap className="max-h-[60vh] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="divide-y-0 hover:bg-transparent">
              <TableHead scope="col" className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={() => onSelectAll(allSelected ? [] : selectableKeys)}
                  aria-label="Select all keyword recommendations"
                />
              </TableHead>
              <TableHead scope="col" className="min-w-[9rem] px-3 py-2">
                Keyword
              </TableHead>
              {isCampaignScoped ? (
                <TableHead scope="col" className="min-w-[9rem] px-3 py-2">
                  Ad group
                </TableHead>
              ) : (
                <TableHead scope="col" className="min-w-[9rem] px-3 py-2">
                  Campaign
                </TableHead>
              )}
              <TableHead scope="col" className="px-3 py-2">
                Match
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                Spend
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                Sales
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                ACOS
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                ROAS
              </TableHead>
              <TableHead scope="col" className="px-3 py-2 text-right">
                Suggested bid
              </TableHead>
              <TableHead scope="col" className="px-3 py-2">
                Type
              </TableHead>
              <TableHead scope="col" className="w-36 px-3 py-2">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <tbody className="divide-y divide-border">
            {filteredRows.map((row) => {
              const key = keywordRowKey(row.keywordId);
              const applyAction = buildApplyAction(row, bidOverrides);
              const recommendedBid =
                bidOverrides[key] ??
                row.recommendation.recommendedBid ??
                row.currentBid;
              const reason = stripDuplicateRoasFromReason(
                row.recommendation.reason,
                row.metrics.roas
              );
              const negativeAlternate = getKeywordNegativeAlternate(row);
              const fingerprint = keywordRecommendationFingerprint(row.keywordId);

              return (
                <TableRow key={key}>
                  <TableCell compact className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(key)}
                      onChange={() => onToggleSelect(key)}
                      disabled={!applyAction}
                      aria-label={`Select ${row.keywordText}`}
                    />
                  </TableCell>
                  <TableCell compact className="px-3 py-2 font-medium text-foreground">
                    {row.keywordText}
                  </TableCell>
                  {isCampaignScoped ? (
                    <TableCell compact className="px-3 py-2 text-foreground-muted">
                      {adGroupLabel(row.adGroupId, adGroupNameById)}
                    </TableCell>
                  ) : (
                    <TableCell compact className="px-3 py-2">
                      <Link
                        to={campaignDetailPath(profileId, row.campaignId)}
                        className="line-clamp-2 text-brand hover:underline"
                        title={row.campaignName || row.campaignId}
                      >
                        {row.campaignName || row.campaignId}
                      </Link>
                    </TableCell>
                  )}
                  <TableCell compact className="px-3 py-2 text-foreground-muted">
                    {row.matchType}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatRecommendationCurrency(row.metrics.cost)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatRecommendationCurrency(row.metrics.sales30d)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatRecommendationPct(row.metrics.acos)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right font-mono tabular-nums">
                    {row.metrics.roas.toFixed(2)}
                  </TableCell>
                  <TableCell compact className="px-3 py-2 text-right">
                    {row.recommendation.recommendedBid != null ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-xs text-foreground-subtle">
                          {row.currentBid.toFixed(2)} →
                        </span>
                        <EditableSuggestedBidCell
                          value={recommendedBid}
                          onChange={(bid) => onBidChange(key, bid)}
                          label="Suggested bid"
                        />
                      </div>
                    ) : (
                      <span className="text-chrome-label">—</span>
                    )}
                  </TableCell>
                  <TableCell compact className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <span className="inline-flex rounded bg-surface-muted px-2 py-0.5 text-xs text-foreground-muted">
                        {actionTypeLabel(row.recommendation.action)}
                      </span>
                      <RecommendationReasonTooltip reason={reason} />
                    </div>
                  </TableCell>
                  <TableCell compact className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      {applyAction ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => onApply(applyAction)}
                          disabled={applying || applyDisabled}
                          title={applyDisabled ? applyDisabledReason : undefined}
                        >
                          Apply
                        </Button>
                      ) : null}
                      {negativeAlternate ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          title={
                            applyDisabled
                              ? applyDisabledReason
                              : negativeAlternate.eligible
                                ? negativeAlternate.reason
                                : 'Exact keywords should be paused, not negated in the same ad group.'
                          }
                          onClick={() => {
                            if (!negativeAlternate.eligible || applyDisabled) return;
                            onAddNegative(row);
                          }}
                          disabled={applying || !negativeAlternate.eligible || applyDisabled}
                        >
                          Add negative
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => onDismiss([fingerprint])}
                        disabled={applying}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {filteredRows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-foreground-subtle">
          No rows match your filters.
        </p>
      ) : null}
    </section>
  );
}
